// Pure FindQuery operations (contract D.1, D.4.1). No React, no I/O.
// Every export is immutable (inputs are never mutated) and never throws:
// bad input is treated as an empty query.
//
// FindQuery = {
//   songs: [{ key, name, slot: 'any'|'opener'|'set2opener'|'setcloser'|'closer'|'encore', jam: null|75|90 }],  // max 12
//   links: [bool],   // length songs.length-1; links[i] true => songs[i] "into" songs[i+1]
//   not: [{ key, name }], sandwiches: [{ outer:{key,name}, inner:{key,name} }],
//   venue: { id, name }|null, city: { key, label }|null, years: [from, to]|null, era: string|null,
//   sbd: bool, vibe: string|null, mode: 'all'|'min'|'closest', minN: number, sort: string }

export const MAX_SONGS = 12;
export const MAX_NOT = 4;
export const MAX_SANDWICHES = 3;

export const SLOTS = ['any', 'opener', 'set2opener', 'setcloser', 'closer', 'encore'];
export const SLOT_LABELS = {
  any: 'Anywhere',
  opener: 'Show opener',
  set2opener: 'Set 2 opener',
  setcloser: 'Set closer',
  closer: 'Show closer',
  encore: 'Encore',
};
export const JAM_LABELS = { 75: 'Top 25%', 90: 'Top 10%' };
export const MODE_LABELS = { all: 'All songs', min: 'At least N', closest: 'Closest' };

export const emptyQuery = () => ({
  songs: [],
  links: [],
  not: [],
  sandwiches: [],
  venue: null,
  city: null,
  years: null,
  era: null,
  sbd: false,
  vibe: null,
  mode: 'all',
  minN: 2,
  sort: 'date',
});

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const hasKey = (v) => isObj(v) && typeof v.key === 'string' && v.key.length > 0;
const nameOf = (v) => (v && typeof v.name === 'string' && v.name ? v.name : v && v.key ? v.key : '');

const normChip = (s) => ({
  ...s,
  key: s.key,
  name: nameOf(s),
  slot: SLOTS.indexOf(s.slot) >= 0 ? s.slot : 'any',
  jam: s.jam === 75 || s.jam === 90 ? s.jam : null,
});

// Keep the songs for which keep(song, index) is true. Links survive only
// between chips that stay adjacent, so removing a chip splits its run.
const keepSongs = (q, keep) => {
  const kept = [];
  q.songs.forEach((s, i) => {
    if (keep(s, i)) kept.push(i);
  });
  const songs = kept.map((i) => q.songs[i]);
  const links = [];
  for (let a = 0; a < kept.length - 1; a++) {
    links.push(kept[a + 1] === kept[a] + 1 && q.links[kept[a]] === true);
  }
  return { songs, links };
};

// Fill defaults, drop malformed chips, fix links length. Returns a new object.
export const normalizeQuery = (q) => {
  const e = emptyQuery();
  if (!isObj(q)) return e;
  const rawSongs = Array.isArray(q.songs) ? q.songs : [];
  const rawLinks = Array.isArray(q.links) ? q.links : [];
  const { songs, links } = keepSongs({ songs: rawSongs, links: rawLinks }, (s) => hasKey(s));
  const years =
    Array.isArray(q.years) && q.years.length === 2 && q.years.every((y) => y == null || Number.isFinite(y)) && q.years.some((y) => y != null)
      ? [q.years[0], q.years[1]]
      : null;
  return {
    ...e,
    songs: songs.slice(0, MAX_SONGS).map(normChip),
    links: links.slice(0, Math.max(0, Math.min(songs.length, MAX_SONGS) - 1)),
    not: (Array.isArray(q.not) ? q.not : []).filter(hasKey).slice(0, MAX_NOT).map((n) => ({ ...n, key: n.key, name: nameOf(n) })),
    sandwiches: (Array.isArray(q.sandwiches) ? q.sandwiches : [])
      .filter((w) => isObj(w) && hasKey(w.outer) && hasKey(w.inner))
      .slice(0, MAX_SANDWICHES)
      .map((w) => ({ outer: { key: w.outer.key, name: nameOf(w.outer) }, inner: { key: w.inner.key, name: nameOf(w.inner) } })),
    venue: isObj(q.venue) && q.venue.id ? { ...q.venue } : null,
    city: isObj(q.city) && q.city.key ? { ...q.city } : null,
    years,
    era: typeof q.era === 'string' && q.era ? q.era : null,
    sbd: !!q.sbd,
    vibe: typeof q.vibe === 'string' && q.vibe ? q.vibe : null,
    mode: q.mode === 'min' || q.mode === 'closest' ? q.mode : 'all',
    minN: Number.isFinite(q.minN) ? Math.min(MAX_SONGS, Math.max(1, Math.round(q.minN))) : 2,
    sort: typeof q.sort === 'string' && q.sort ? q.sort : 'date',
  };
};

// Maximal linked runs: [{ start, end, keys }] with end inclusive, 2+ songs.
export const linkedRuns = (q) => {
  const n = normalizeQuery(q);
  const runs = [];
  let start = null;
  n.songs.forEach((s, i) => {
    if (start === null) start = i;
    if (!(i < n.songs.length - 1 && n.links[i] === true)) {
      if (i > start) runs.push({ start, end: i, keys: n.songs.slice(start, i + 1).map((x) => x.key) });
      start = null;
    }
  });
  return runs;
};

// All song keys the query asks for (songs and seq runs), distinct, in order.
// Used for `.track-row.match`.
export const queryKeys = (q) => {
  if (!isObj(q) || !Array.isArray(q.songs)) return [];
  const seen = {};
  const out = [];
  q.songs.forEach((s) => {
    if (hasKey(s) && !seen[s.key]) {
      seen[s.key] = true;
      out.push(s.key);
    }
  });
  return out;
};

// True when the query has no chips and no filters (mode and sort do not count).
export const isEmpty = (q) => {
  if (!isObj(q)) return true;
  const n = normalizeQuery(q);
  return (
    n.songs.length === 0 &&
    n.not.length === 0 &&
    n.sandwiches.length === 0 &&
    !n.venue &&
    !n.city &&
    !n.years &&
    !n.era &&
    !n.sbd &&
    !n.vibe
  );
};

const splitOnce = (v, sep, last) => {
  const at = last ? v.lastIndexOf(sep) : v.indexOf(sep);
  return at < 0 ? [v, ''] : [v.slice(0, at), v.slice(at + sep.length)];
};

const unlinkSeq = (n, value) => {
  const links = n.links.slice();
  const runs = linkedRuns(n);
  const exact = runs.filter((r) => r.keys.join('>') === value);
  if (exact.length) {
    exact.forEach((r) => {
      for (let i = r.start; i < r.end; i++) links[i] = false;
    });
  } else {
    // not an exact run: unlink any linked pair that is consecutive in value
    const keys = value.split('>');
    const pairs = {};
    for (let i = 0; i < keys.length - 1; i++) pairs[keys[i] + '>' + keys[i + 1]] = true;
    for (let i = 0; i < links.length; i++) {
      if (links[i] && pairs[n.songs[i].key + '>' + n.songs[i + 1].key]) links[i] = false;
    }
  }
  return { ...n, links };
};

// Apply one B.3 `dropOne` entry { kind, value }:
//   song      remove that chip everywhere (its runs split, its slot and jam go with it)
//   seq       unlink that run; the songs stay as plain chips
//   slot      'opener:k' -> that chip's slot becomes 'any'
//   jam       'k:90'     -> that chip's jam becomes null
//   sandwich  'outer~inner' removed
//   not       songKey removed from not
//   venue, city   cleared
//   years     clears both years and era
//   sbd       false
//   vibe      cleared
// Unknown kinds return the (normalized) query unchanged.
export const applyDrop = (q, entry) => {
  const n = normalizeQuery(q);
  if (!isObj(entry)) return n;
  const value = entry.value == null ? '' : String(entry.value);
  switch (entry.kind) {
    case 'song':
      return { ...n, ...keepSongs(n, (s) => s.key !== value) };
    case 'seq':
      return unlinkSeq(n, value);
    case 'slot': {
      const [slot, key] = splitOnce(value, ':', false);
      return { ...n, songs: n.songs.map((s) => (s.key === key && s.slot === slot ? { ...s, slot: 'any' } : s)) };
    }
    case 'jam': {
      const [key, pct] = splitOnce(value, ':', true);
      const jam = parseInt(pct, 10);
      return {
        ...n,
        songs: n.songs.map((s) => (s.key === key && s.jam != null && (!Number.isFinite(jam) || s.jam === jam) ? { ...s, jam: null } : s)),
      };
    }
    case 'sandwich': {
      const [outer, inner] = splitOnce(value, '~', false);
      return { ...n, sandwiches: n.sandwiches.filter((w) => !(w.outer.key === outer && w.inner.key === inner)) };
    }
    case 'not':
      return { ...n, not: n.not.filter((x) => x.key !== value) };
    case 'venue':
      return { ...n, venue: null };
    case 'city':
      return { ...n, city: null };
    case 'years':
    case 'era':
      return { ...n, years: null, era: null };
    case 'sbd':
      return { ...n, sbd: false };
    case 'vibe':
      return { ...n, vibe: null };
    default:
      return n;
  }
};

// Remove one ActiveFilterChips part. `part` is a queryParts() element
// ({ kind, value, index? }) or any B.3 dropOne entry. With an `index`,
// 'song' / 'slot' / 'jam' act on that one chip and 'link' unlinks
// links[index]; 'mode' resets to all songs; 'sort' to date; everything else
// behaves like applyDrop.
export const removePart = (q, part) => {
  const n = normalizeQuery(q);
  if (!isObj(part)) return n;
  const i = Number.isInteger(part.index) ? part.index : -1;
  const chipOk = i >= 0 && i < n.songs.length;
  switch (part.kind) {
    case 'song':
      if (chipOk && (part.value == null || n.songs[i].key === String(part.value))) {
        return { ...n, ...keepSongs(n, (s, j) => j !== i) };
      }
      return applyDrop(n, part);
    case 'slot':
      if (chipOk) return { ...n, songs: n.songs.map((s, j) => (j === i ? { ...s, slot: 'any' } : s)) };
      return applyDrop(n, part);
    case 'jam':
      if (chipOk) return { ...n, songs: n.songs.map((s, j) => (j === i ? { ...s, jam: null } : s)) };
      return applyDrop(n, part);
    case 'link':
      if (i >= 0 && i < n.links.length) {
        const links = n.links.slice();
        links[i] = false;
        return { ...n, links };
      }
      return n;
    case 'mode':
      return { ...n, mode: 'all' };
    case 'sort':
      return { ...n, sort: 'date' };
    default:
      return applyDrop(n, part);
  }
};

// Readable parts for ActiveFilterChips: [{ kind, value, index?, label }].
// `meta` (B.2) is optional and only supplies era and vibe labels.
export const queryParts = (q, meta) => {
  const n = normalizeQuery(q);
  const parts = [];
  const features = (meta && meta.features) || {};
  n.songs.forEach((s, index) => {
    const extra = [];
    if (s.slot !== 'any') extra.push(SLOT_LABELS[s.slot]);
    if (s.jam) extra.push('long, ' + JAM_LABELS[s.jam].toLowerCase());
    parts.push({ kind: 'song', value: s.key, index, label: s.name + (extra.length ? ' (' + extra.join(', ') + ')' : '') });
  });
  linkedRuns(n).forEach((r) =>
    parts.push({
      kind: 'seq',
      value: r.keys.join('>'),
      label: n.songs
        .slice(r.start, r.end + 1)
        .map((s) => s.name)
        .join(' into '),
    })
  );
  n.not.forEach((x) => parts.push({ kind: 'not', value: x.key, label: 'Not ' + x.name }));
  n.sandwiches.forEach((w) =>
    parts.push({ kind: 'sandwich', value: w.outer.key + '~' + w.inner.key, label: w.outer.name + ' around ' + w.inner.name })
  );
  if (n.venue) parts.push({ kind: 'venue', value: n.venue.id, label: n.venue.name || n.venue.id });
  if (n.city) parts.push({ kind: 'city', value: n.city.key, label: n.city.label || n.city.key });
  if (n.era || n.years) {
    const era = n.era && Array.isArray(meta && meta.eras) ? meta.eras.find((e) => e.key === n.era) : null;
    const range = n.years ? [n.years[0], n.years[1]].filter((y) => y != null).join('-') : '';
    parts.push({ kind: 'years', value: range || n.era, label: era ? era.label : range || n.era });
  }
  if (n.sbd) parts.push({ kind: 'sbd', value: '1', label: 'Soundboard' });
  if (n.vibe) {
    const v = Array.isArray(features.vibes) ? features.vibes.find((x) => x.key === n.vibe) : null;
    parts.push({ kind: 'vibe', value: n.vibe, label: v ? v.label : n.vibe });
  }
  if (n.mode === 'min') parts.push({ kind: 'mode', value: 'min:' + n.minN, label: 'At least ' + n.minN });
  if (n.mode === 'closest') parts.push({ kind: 'mode', value: 'closest', label: 'Closest' });
  return parts;
};

// ---- builder edits --------------------------------------------------------

// Append a chip. `linked` links it "into" the previous last chip. Without
// `allowDuplicate`, a key already present is not added again. Full queries
// (12 chips) come back unchanged.
export const addSong = (q, song, { linked = false, allowDuplicate = false } = {}) => {
  const n = normalizeQuery(q);
  if (!hasKey(song) || n.songs.length >= MAX_SONGS) return n;
  if (!allowDuplicate && n.songs.some((s) => s.key === song.key)) return n;
  const chip = normChip({ key: song.key, name: nameOf(song), slot: song.slot, jam: song.jam });
  const links = n.songs.length ? n.links.concat([!!linked]) : [];
  return { ...n, songs: n.songs.concat([chip]), links };
};

export const removeSongAt = (q, index) => removePart(q, { kind: 'song', index });

export const updateSongAt = (q, index, patch) => {
  const n = normalizeQuery(q);
  if (!(index >= 0 && index < n.songs.length) || !isObj(patch)) return n;
  return { ...n, songs: n.songs.map((s, j) => (j === index ? normChip({ ...s, ...patch, key: s.key }) : s)) };
};

export const setLinkAt = (q, index, linked) => {
  const n = normalizeQuery(q);
  if (!(index >= 0 && index < n.links.length)) return n;
  const links = n.links.slice();
  links[index] = !!linked;
  return { ...n, links };
};

// Move chip `index` by dir (-1 left, +1 right). Links touching the moved chip
// are cleared, since the "into" relation no longer holds.
export const moveSong = (q, index, dir) => {
  const n = normalizeQuery(q);
  const j = index + (dir < 0 ? -1 : 1);
  if (!(index >= 0 && index < n.songs.length) || j < 0 || j >= n.songs.length) return n;
  const songs = n.songs.slice();
  const t = songs[index];
  songs[index] = songs[j];
  songs[j] = t;
  const lo = Math.min(index, j);
  const links = n.links.slice();
  [lo - 1, lo, lo + 1].forEach((k) => {
    if (k >= 0 && k < links.length) links[k] = false;
  });
  return { ...n, songs, links };
};

// ---- Vibe Check merge -----------------------------------------------------

// Merge a Vibe Check `query` (B.11, always a full FindQuery) into `q`.
// Chips: union by key. A vibe chip whose key is already present keeps the
// existing chip, but takes the vibe's slot / jam when those are set. Vibe
// links are kept wherever both ends end up adjacent. not and sandwiches:
// union. Scalar filters are replaced only where the vibe query sets them
// (venue, city, years+era as one group, sbd when true, vibe, mode+minN when
// not 'all', sort when not 'date'), so an unset vibe field never wipes a
// filter the listener already chose.
export const mergeVibe = (q, vibeQuery) => {
  const n = normalizeQuery(q);
  const v = normalizeQuery(vibeQuery);
  const songs = n.songs.slice();
  const links = n.links.slice();
  let prev = -1; // merged index of the previous vibe chip
  v.songs.forEach((s, j) => {
    const wantLink = j > 0 && v.links[j - 1] === true;
    let at = songs.findIndex((x) => x.key === s.key);
    if (at >= 0) {
      const patch = {};
      if (s.slot !== 'any') patch.slot = s.slot;
      if (s.jam != null) patch.jam = s.jam;
      if (Object.keys(patch).length) songs[at] = { ...songs[at], ...patch };
      if (wantLink && prev >= 0 && at === prev + 1) links[prev] = true;
    } else if (songs.length < MAX_SONGS) {
      if (songs.length) links.push(wantLink && prev === songs.length - 1);
      songs.push(s);
      at = songs.length - 1;
    } else {
      at = -1;
    }
    prev = at;
  });

  const not = n.not.slice();
  v.not.forEach((x) => {
    if (not.length < MAX_NOT && !not.some((y) => y.key === x.key)) not.push(x);
  });
  const sandwiches = n.sandwiches.slice();
  v.sandwiches.forEach((w) => {
    if (sandwiches.length < MAX_SANDWICHES && !sandwiches.some((y) => y.outer.key === w.outer.key && y.inner.key === w.inner.key)) {
      sandwiches.push(w);
    }
  });

  const out = { ...n, songs, links, not, sandwiches };
  if (v.venue) out.venue = v.venue;
  if (v.city) out.city = v.city;
  if (v.years || v.era) {
    out.years = v.years;
    out.era = v.era;
  }
  if (v.sbd) out.sbd = true;
  if (v.vibe) out.vibe = v.vibe;
  if (v.mode !== 'all') {
    out.mode = v.mode;
    out.minN = v.minN;
  }
  if (v.sort !== 'date') out.sort = v.sort;
  return out;
};

// ---- builder helpers (WEB-SEARCH, additive to the step-0 surface) ---------

// Server caps on repeated find params (contract B.3). The builder keeps
// queries inside these so /ds/find never answers 400 to a query the UI built.
export const MAX_SEQ_RUNS = 4; // seq=    (linked runs of 2+ chips)
export const MAX_RUN_KEYS = 5; // keys in one seq run
export const MAX_SLOTS = 6; // slot=   (chips with slot !== 'any')
export const MAX_JAMS = 4; // jam=    (chips with a jam percentile)

// The linked run that contains chip `index`: { start, end } (inclusive). An
// unlinked chip is its own run (start === end). Out of range: null.
export const runAround = (q, index) => {
  const n = normalizeQuery(q);
  if (!(index >= 0 && index < n.songs.length)) return null;
  let start = index;
  while (start > 0 && n.links[start - 1] === true) start--;
  let end = index;
  while (end < n.songs.length - 1 && n.links[end] === true) end++;
  return { start, end };
};

export const slotCount = (q) => normalizeQuery(q).songs.filter((s) => s.slot !== 'any').length;
export const jamCount = (q) => normalizeQuery(q).songs.filter((s) => s.jam != null).length;

// Why links[index] cannot be turned on, or null when it can:
//   'range'    no such connector
//   'runKeys'  the merged run would hold more than MAX_RUN_KEYS songs
//   'runs'     the query would hold more than MAX_SEQ_RUNS linked runs
export const linkBlocked = (q, index) => {
  const n = normalizeQuery(q);
  if (!(index >= 0 && index < n.links.length)) return 'range';
  if (n.links[index] === true) return null;
  const links = n.links.slice();
  links[index] = true;
  const runs = linkedRuns({ ...n, links });
  if (runs.some((r) => r.keys.length > MAX_RUN_KEYS)) return 'runKeys';
  if (runs.length > MAX_SEQ_RUNS) return 'runs';
  return null;
};

// Insert a chip right after chip `index`. `linked` links chip `index` into
// the new chip. The new chip is never linked onward (what followed it is not
// known), so a run that went on past `index` is split there. With index =
// last chip this is addSong. Without `allowDuplicate`, a key already present
// is not added again; full queries come back unchanged.
export const insertSongAfter = (q, index, song, { linked = false, allowDuplicate = false } = {}) => {
  const n = normalizeQuery(q);
  if (!hasKey(song) || n.songs.length >= MAX_SONGS) return n;
  if (!(index >= 0 && index < n.songs.length)) return addSong(n, song, { linked, allowDuplicate });
  if (!allowDuplicate && n.songs.some((s) => s.key === song.key)) return n;
  const chip = normChip({ key: song.key, name: nameOf(song), slot: song.slot, jam: song.jam });
  const songs = n.songs.slice(0, index + 1).concat([chip], n.songs.slice(index + 1));
  const links = n.links.slice(0, index).concat([!!linked]);
  if (index < n.songs.length - 1) links.push(false);
  return { ...n, songs, links: links.concat(n.links.slice(index + 1)) };
};
